import { AI_AGENTS_INFO, AiAgent } from './aiAgentsConsts';
import { AgentType, ChatCompletionRequestMessageRole } from '@server/ai';
import isNil from 'lodash/isNil';
import { AiChatMessage } from '../common/ai-chat/aiChatTypes';
import { appRoutes } from '../../constants/appRoutes';
import { addCrmOrgIdToPath } from '../../navigation-utils/urlOrgUtils';
import { readableUUID } from '../../navigation-utils/readableUUID';
import { isTempChatId } from './agentsChatsReducer';

export const isMessagesPaywallLimitReached = ({
  messages,
  agentType,
}: {
  messages?: AiChatMessage[];
  agentType?: AgentType;
}) => {
  const { paywallNumberOfAllowedUserMessages_deprecated } =
    AI_AGENTS_INFO[agentType ?? AgentType.Documentation];

  const userMessagesCount = messages?.filter(
    (msg) => msg.role === ChatCompletionRequestMessageRole.USER,
  ).length;

  return !isNil(userMessagesCount) && !isNil(paywallNumberOfAllowedUserMessages_deprecated)
    ? userMessagesCount >= paywallNumberOfAllowedUserMessages_deprecated
    : false;
};

/**
 * Checks if an agent is a metadata type agent
 */
export const isMetadataTypeAgent = (agent: AiAgent | null | undefined): boolean => {
  return !!getAgentMetadataType(agent);
};

/**
 * Gets the metadataType from an agent if it exists
 */
export const getAgentMetadataType = (agent: AiAgent | null | undefined): string | undefined => {
  return agent?.type === AgentType.MonitoringV2 &&
    agent?.contextComponents &&
    'metadataType' in agent.contextComponents
    ? agent.contextComponents.metadataType
    : undefined;
};

/**
 * Generates a URL for agent/chat navigation
 */
export const generateAgentChatUrl = ({
  agentId,
  chatId,
  crmOrgId,
  isShareable = false,
  includeOrigin = false,
  excludeReadableId = false,
  accountId,
}: {
  agentId?: string;
  chatId?: string;
  crmOrgId?: string;
  isShareable?: boolean;
  includeOrigin?: boolean;
  excludeReadableId?: boolean;
  accountId: string;
}): string => {
  let destination =
    addCrmOrgIdToPath(appRoutes.agenticLayer.route, accountId, crmOrgId) ??
    appRoutes.agenticLayer.route;

  if (agentId) {
    const readableAgentId = excludeReadableId ? agentId : readableUUID(agentId);
    if (readableAgentId) {
      destination += `/agent/${readableAgentId}`;
    }
  }

  if (chatId) {
    let chatIdToUse: string;
    if (isShareable) {
      // For shareable URLs, use the original chatId
      chatIdToUse = chatId;
    } else {
      // For navigation URLs, use readable chatId (or keep temp chat as-is)
      const isTempChat = isTempChatId(chatId);
      chatIdToUse = isTempChat ? chatId : (readableUUID(chatId) ?? chatId);
    }
    destination += `/chat/${chatIdToUse}`;
  }

  return includeOrigin ? `${window.location.origin}${destination}` : destination;
};
